import React, { useCallback, useState } from "react";
import { AutoAwesome } from "@mui/icons-material";
import { Tooltip, styled } from "@mui/material";
import GlowBorder from "../common/GlowBorder";
import { useContextAgent } from "./ContextAgentContext";
import "./ContextAgentSidebarTab.css";

const TAB_WIDTH = 38;
const TAB_HEIGHT = 56;
const TAB_RADIUS = 6;
const TAB_BORDER_RADIUS = `0 ${TAB_RADIUS}px ${TAB_RADIUS}px 0`;
const AI_BUTTON_SIZE = 30;

const PULL_OUT_ANIMATION = "context-agent-tab-pull-out";
const PULL_IN_ANIMATION = "context-agent-tab-pull-in";

const TabBody = styled("span")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
  boxSizing: "border-box",
  borderRadius: TAB_BORDER_RADIUS,
});

const AgentTabRoot = styled("button")(({ theme }) => ({
  position: "absolute",
  left: 0,
  top: "50%",
  zIndex: 1200,
  height: TAB_HEIGHT,
  padding: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  border: "none",
  background: "transparent",
  pointerEvents: "auto",
  overflow: "visible",
  borderRadius: TAB_BORDER_RADIUS,
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

/** Sidebar seam tab — animated AI button (GlowBorder) at the main-column edge. */
export default function ContextAgentSidebarTab() {
  const { registration, open, toggleOpen, isRegistered } = useContextAgent();
  const [isExiting, setIsExiting] = useState(false);

  const handleClick = useCallback(() => {
    if (isExiting) {
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      toggleOpen();
      return;
    }
    setIsExiting(true);
  }, [isExiting, toggleOpen]);

  const handleAnimationEnd = useCallback(
    (event) => {
      if (event.currentTarget !== event.target) {
        return;
      }

      const { animationName } = event;
      if (animationName === PULL_IN_ANIMATION && isExiting) {
        setIsExiting(false);
        toggleOpen();
        return;
      }

      if (animationName === PULL_OUT_ANIMATION && !isExiting) {
        event.currentTarget.classList.remove("context-agent-sidebar-tab--enter");
      }
    },
    [isExiting, toggleOpen]
  );

  if (!isRegistered || !registration) {
    return null;
  }

  if (open && !isExiting) {
    return null;
  }

  const { agentName, scopeLabel } = registration;
  const tooltip = scopeLabel ? `${agentName} · ${scopeLabel}` : agentName;

  const tabClassName = isExiting
    ? "context-agent-sidebar-tab--exit"
    : "context-agent-sidebar-tab--enter";

  return (
    <Tooltip title={tooltip} placement="right" arrow>
      <AgentTabRoot
        type="button"
        className={`context-agent-sidebar-tab ${tabClassName}`}
        style={{ "--context-agent-tab-width": `${TAB_WIDTH}px` }}
        aria-label={`Open ${agentName}`}
        aria-pressed={false}
        onClick={handleClick}
        onAnimationEnd={handleAnimationEnd}
        data-testid="context-agent-sidebar-tab"
      >
        <TabBody>
          <GlowBorder
            size={AI_BUTTON_SIZE}
            borderRadius={5}
            className="context-agent-sidebar-tab__ai-button"
          >
            <AutoAwesome sx={{ color: "#FFF", fontSize: 16 }} />
          </GlowBorder>
        </TabBody>
      </AgentTabRoot>
    </Tooltip>
  );
}
